// src/utils/darkModeAdapter.js
// ダークモード対応の共通カラーユーティリティ
// ハードコードされた色をテーマモードに応じて動的に変換する

/**
 * テーマモードに応じた色を返す
 * @param {Object} theme - MUIテーマオブジェクト
 * @param {string} lightColor - ライトモード時の色
 * @param {string} darkColor - ダークモード時の色
 * @returns {string} 現在のモードに応じた色
 */
export function adaptiveColor(theme, lightColor, darkColor) {
  return theme.palette.mode === 'dark' ? darkColor : lightColor;
}

/**
 * テーマモードに応じた背景色プリセット
 */
export function bg(theme) {
  const isDark = theme.palette.mode === 'dark';
  return {
    // 純白背景 → ダーク時paper色
    white: isDark ? theme.palette.background.paper : '#ffffff',
    // メインのデフォルト背景
    default: theme.palette.background.default,
    // 薄いグレー背景 → ダーク時は暗めのサーフェス
    subtle: isDark ? 'rgba(255,255,255,0.04)' : '#fafafa',
    // カード・フォーム背景
    card: isDark ? theme.palette.background.paper : '#ffffff',
    // hover状態
    hover: isDark ? 'rgba(255,255,255,0.08)' : '#F0F2F5',
    // 薄い色付き背景
    tinted: isDark ? 'rgba(255,255,255,0.06)' : '#f5f5f5',
  };
}

/**
 * テーマモードに応じたテキスト色プリセット
 */
export function text(theme) {
  const isDark = theme.palette.mode === 'dark';
  return {
    primary: isDark ? '#E8E8E8' : '#333333',
    secondary: isDark ? '#B0B0B0' : '#555555',
    muted: isDark ? '#8B95A3' : '#666666',
    disabled: isDark ? '#6B7585' : '#9e9e9e',
  };
}

/**
 * テーマモードに応じたボーダー色プリセット
 */
export function border(theme) {
  const isDark = theme.palette.mode === 'dark';
  return {
    default: isDark ? 'rgba(255,255,255,0.12)' : '#e0e0e0',
    light: isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0',
    subtle: isDark ? 'rgba(255,255,255,0.06)' : '#e5e7eb',
  };
}

/**
 * テーマモードに応じたシャドウ
 */
export function shadow(theme) {
  const isDark = theme.palette.mode === 'dark';
  return {
    sm: isDark
      ? '0 1px 3px rgba(0,0,0,0.4)'
      : '0 1px 3px rgba(0,0,0,0.12)',
    md: isDark
      ? '0 4px 6px rgba(0,0,0,0.4)'
      : '0 4px 6px rgba(0,0,0,0.1)',
    lg: isDark
      ? '0 10px 15px rgba(0,0,0,0.5)'
      : '0 10px 15px rgba(0,0,0,0.1)',
    card: isDark
      ? '0 4px 12px rgba(0,0,0,0.4)'
      : '0 4px 12px rgba(0,0,0,0.08)',
  };
}

/**
 * テーマモードに応じたグラデーション背景
 */
export function gradient(theme) {
  const isDark = theme.palette.mode === 'dark';
  return {
    // 白→薄いグレーのグラデーション
    subtle: isDark
      ? 'linear-gradient(135deg, #1E1E1E 0%, #252525 100%)'
      : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
    // カード背景グラデーション
    card: isDark
      ? 'linear-gradient(135deg, #1E1E1E 0%, #2A2A2A 100%)'
      : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
    // メニュー背景グラデーション
    menu: isDark
      ? 'linear-gradient(170deg, #1E1E1E 0%, #252530 100%)'
      : 'linear-gradient(170deg, #ffffff 0%, #f8f9ff 100%)',
    // プライマリ色のグラデーション
    primary: isDark
      ? `linear-gradient(135deg, rgba(91, 184, 240, 0.08) 0%, rgba(21, 165, 176, 0.12) 100%)`
      : `linear-gradient(135deg, rgba(63, 81, 181, 0.05) 0%, rgba(33, 150, 243, 0.1) 100%)`,
  };
}

/**
 * テーマモードに応じたオーバーレイ・半透明背景
 */
export function overlay(theme) {
  const isDark = theme.palette.mode === 'dark';
  return {
    // 極薄の黒 → ダーク時は極薄の白
    lightest: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
    light: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
    medium: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
    // 半透明の白 → ダーク時は半透明の暗色
    whiteLight: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.5)',
    whiteMedium: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.7)',
  };
}

/**
 * セマンティックカラーのlight背景（ステータス表示など）
 */
export function statusBg(theme) {
  const isDark = theme.palette.mode === 'dark';
  return {
    success: isDark ? 'rgba(76,175,80,0.12)' : '#F0FDF4',
    error: isDark ? 'rgba(244,67,54,0.12)' : '#FEF2F2',
    warning: isDark ? 'rgba(255,152,0,0.12)' : '#fff3e0',
    info: isDark ? 'rgba(33,150,243,0.12)' : '#e3f2fd',
    purple: isDark ? 'rgba(156,39,176,0.12)' : '#F5F3FF',
  };
}

/**
 * セマンティックカラーのborder
 */
export function statusBorder(theme) {
  const isDark = theme.palette.mode === 'dark';
  return {
    success: isDark ? 'rgba(76,175,80,0.3)' : '#BBF7D0',
    error: isDark ? 'rgba(244,67,54,0.3)' : '#FECACA',
    warning: isDark ? 'rgba(255,152,0,0.3)' : 'rgba(255,152,0,0.2)',
    info: isDark ? 'rgba(33,150,243,0.3)' : 'rgba(25,118,210,0.1)',
  };
}

/**
 * グラスモーフィズム効果をダークモードに適応
 */
export function glassmorphism(theme) {
  const isDark = theme.palette.mode === 'dark';
  return {
    background: isDark ? 'rgba(30, 30, 30, 0.85)' : 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(8px)',
    border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.08)',
  };
}
